/**
 * Editor state.
 *
 * Pattern data lives in typed arrays and is mutated in place rather than
 * replaced — a 64×32 pattern is 10 KB and the editor touches it on every
 * keystroke, so a `revision` counter drives redraws instead of immutable copies.
 */
import { create } from 'zustand';
import {
  CELL_EFFECT,
  CELL_INSTRUMENT,
  CELL_NOTE,
  CELL_PARAM,
  CELL_STRIDE,
  CELL_VOLUME,
  createInstrument,
  createPattern,
  type LoadWarning,
  type Pattern,
  type Song,
} from '../engine/types';
import { DEFAULT_MIXER_SETTINGS, type MixerSettings } from '../engine/dsp/mixer';
import { createEmptySong } from '../engine/loaders';
import { audioEngine } from '../engine/audio';

/** Sub-columns within one channel, in cursor order. */
export const COL_NOTE = 0;
export const COL_INSTRUMENT_HI = 1;
export const COL_INSTRUMENT_LO = 2;
export const COL_VOLUME_HI = 3;
export const COL_VOLUME_LO = 4;
export const COL_EFFECT = 5;
export const COL_PARAM_HI = 6;
export const COL_PARAM_LO = 7;
export const COLUMNS_PER_CHANNEL = 8;

/** MOD has no volume column, so the cursor skips it entirely. */
export function visibleColumns(format: Song['format']): number[] {
  if (format === 'mod') {
    return [COL_NOTE, COL_INSTRUMENT_HI, COL_INSTRUMENT_LO, COL_EFFECT, COL_PARAM_HI, COL_PARAM_LO];
  }
  return [
    COL_NOTE,
    COL_INSTRUMENT_HI,
    COL_INSTRUMENT_LO,
    COL_VOLUME_HI,
    COL_VOLUME_LO,
    COL_EFFECT,
    COL_PARAM_HI,
    COL_PARAM_LO,
  ];
}

export interface Selection {
  startRow: number;
  startChannel: number;
  endRow: number;
  endChannel: number;
}

export interface PlaybackState {
  playing: boolean;
  order: number;
  pattern: number;
  row: number;
  speed: number;
  tempo: number;
  peaks: Float32Array;
  scopes: Float32Array;
  scopeLength: number;
  masterPeak: [number, number];
}

interface UndoEntry {
  label: string;
  patternIndex: number;
  rows: number;
  data: Uint8Array;
}

export interface TrackerStore {
  song: Song;
  fileName: string;
  warnings: LoadWarning[];
  /** Bumped whenever pattern bytes change, so the canvas knows to repaint. */
  revision: number;
  dirty: boolean;

  playback: PlaybackState;

  currentOrder: number;
  cursorRow: number;
  cursorChannel: number;
  cursorColumn: number;
  selection: Selection | null;
  selectionAnchor: { row: number; channel: number } | null;

  editMode: boolean;
  octave: number;
  editStep: number;
  currentInstrument: number;
  followPlayback: boolean;

  mixerSettings: MixerSettings;
  masterVolume: number;
  loopSong: boolean;
  modPanningCommand: boolean;

  soloChannel: number | null;
  mutedChannels: boolean[];

  undoStack: UndoEntry[];
  redoStack: UndoEntry[];

  clipboard: { rows: number; channels: number; data: Uint8Array } | null;

  // --- actions ----------------------------------------------------------
  loadSong(song: Song, fileName: string, warnings: LoadWarning[]): void;
  newSong(): void;
  setPlayback(state: Partial<PlaybackState>): void;

  currentPatternIndex(): number;
  currentPattern(): Pattern;

  setCursor(row: number, channel: number, column: number): void;
  moveCursor(dRow: number, dColumn: number): void;
  setCurrentOrder(order: number): void;
  setSelection(selection: Selection | null): void;
  beginSelection(row: number, channel: number): void;
  extendSelection(row: number, channel: number): void;
  selectAll(): void;

  setEditMode(on: boolean): void;
  setOctave(octave: number): void;
  setEditStep(step: number): void;
  setCurrentInstrument(index: number): void;
  setFollowPlayback(on: boolean): void;

  writeCell(row: number, channel: number, field: number, value: number, label?: string): void;
  writeNote(row: number, channel: number, note: number, instrument: number): void;
  clearCell(row: number, channel: number, column: number): void;
  insertRow(): void;
  deleteRow(): void;
  transposeSelection(semitones: number): void;

  copySelection(): void;
  cutSelection(): void;
  pasteClipboard(): void;
  clearSelection(): void;

  undo(): void;
  redo(): void;
  pushUndo(label: string): void;

  setMixerSettings(settings: Partial<MixerSettings>): void;
  setMasterVolume(value: number): void;
  setLoopSong(on: boolean): void;
  setModPanningCommand(on: boolean): void;
  toggleMute(channel: number): void;
  toggleSolo(channel: number): void;

  setSongName(name: string): void;
  setInitialSpeed(speed: number): void;
  setInitialTempo(tempo: number): void;
  setOrderEntry(index: number, pattern: number): void;
  setSongLength(length: number): void;
  setRestartPosition(position: number): void;
  addPattern(): number;
  addInstrument(): void;
  replaceInstrument(index: number, instrument: Song['instruments'][number]): void;
  markDirty(): void;
}

const MAX_UNDO = 100;

function emptyPlayback(): PlaybackState {
  return {
    playing: false,
    order: 0,
    pattern: 0,
    row: 0,
    speed: 6,
    tempo: 125,
    peaks: new Float32Array(0),
    scopes: new Float32Array(0),
    scopeLength: 0,
    masterPeak: [0, 0],
  };
}

export const useTracker = create<TrackerStore>((set, get) => ({
  song: createEmptySong(),
  fileName: 'untitled.xm',
  warnings: [],
  revision: 0,
  dirty: false,

  playback: emptyPlayback(),

  currentOrder: 0,
  cursorRow: 0,
  cursorChannel: 0,
  cursorColumn: COL_NOTE,
  selection: null,
  selectionAnchor: null,

  editMode: false,
  octave: 4,
  editStep: 1,
  currentInstrument: 1,
  followPlayback: true,

  mixerSettings: { ...DEFAULT_MIXER_SETTINGS },
  masterVolume: 1,
  loopSong: true,
  modPanningCommand: true,

  soloChannel: null,
  mutedChannels: [],

  undoStack: [],
  redoStack: [],
  clipboard: null,

  // ---------------------------------------------------------------------

  loadSong(song, fileName, warnings) {
    set({
      song,
      fileName,
      warnings,
      revision: get().revision + 1,
      dirty: false,
      currentOrder: 0,
      cursorRow: 0,
      cursorChannel: 0,
      cursorColumn: COL_NOTE,
      selection: null,
      selectionAnchor: null,
      currentInstrument: 1,
      undoStack: [],
      redoStack: [],
      mutedChannels: new Array(song.channels).fill(false),
      soloChannel: null,
    });
    audioEngine.loadSong(song);
    audioEngine.clearMutes();
  },

  newSong() {
    get().loadSong(createEmptySong(), 'untitled.xm', []);
  },

  setPlayback(state) {
    set({ playback: { ...get().playback, ...state } });
  },

  currentPatternIndex() {
    const { song, currentOrder } = get();
    return song.order[currentOrder] ?? 0;
  },

  currentPattern() {
    const { song } = get();
    const index = get().currentPatternIndex();
    return song.patterns[index] ?? song.patterns[0];
  },

  setCursor(row, channel, column) {
    const { song } = get();
    const pattern = get().currentPattern();
    const cols = visibleColumns(song.format);
    set({
      cursorRow: Math.max(0, Math.min(pattern.rows - 1, row)),
      cursorChannel: Math.max(0, Math.min(song.channels - 1, channel)),
      cursorColumn: cols.includes(column) ? column : cols[0],
    });
  },

  moveCursor(dRow, dColumn) {
    const { song, cursorRow, cursorChannel, cursorColumn } = get();
    const pattern = get().currentPattern();
    const cols = visibleColumns(song.format);

    let row = cursorRow + dRow;
    let channel = cursorChannel;
    let colIndex = cols.indexOf(cursorColumn);
    if (colIndex < 0) colIndex = 0;
    colIndex += dColumn;

    // Walking off either end of a channel's columns moves to the neighbour.
    while (colIndex < 0) {
      channel--;
      colIndex += cols.length;
    }
    while (colIndex >= cols.length) {
      channel++;
      colIndex -= cols.length;
    }
    if (channel < 0) {
      channel = song.channels - 1;
    } else if (channel >= song.channels) {
      channel = 0;
    }

    // Rows wrap within the pattern, as they do in every tracker.
    if (row < 0) row = pattern.rows + (row % pattern.rows);
    if (row >= pattern.rows) row %= pattern.rows;

    set({ cursorRow: row, cursorChannel: channel, cursorColumn: cols[colIndex] });
  },

  setCurrentOrder(order) {
    const { song } = get();
    const clamped = Math.max(0, Math.min(song.songLength - 1, order));
    const pattern = song.patterns[song.order[clamped] ?? 0] ?? song.patterns[0];
    set({
      currentOrder: clamped,
      cursorRow: Math.min(get().cursorRow, pattern.rows - 1),
      selection: null,
    });
  },

  setSelection(selection) {
    set({ selection });
  },

  beginSelection(row, channel) {
    set({ selectionAnchor: { row, channel }, selection: { startRow: row, startChannel: channel, endRow: row, endChannel: channel } });
  },

  extendSelection(row, channel) {
    const anchor = get().selectionAnchor;
    if (!anchor) return;
    set({
      selection: {
        startRow: Math.min(anchor.row, row),
        startChannel: Math.min(anchor.channel, channel),
        endRow: Math.max(anchor.row, row),
        endChannel: Math.max(anchor.channel, channel),
      },
    });
  },

  selectAll() {
    const { song } = get();
    const pattern = get().currentPattern();
    set({
      selection: {
        startRow: 0,
        startChannel: 0,
        endRow: pattern.rows - 1,
        endChannel: song.channels - 1,
      },
    });
  },

  setEditMode(on) {
    set({ editMode: on });
  },
  setOctave(octave) {
    set({ octave: Math.max(0, Math.min(7, octave)) });
  },
  setEditStep(step) {
    set({ editStep: Math.max(0, Math.min(16, step)) });
  },
  setCurrentInstrument(index) {
    set({ currentInstrument: Math.max(1, Math.min(get().song.instruments.length, index)) });
  },
  setFollowPlayback(on) {
    set({ followPlayback: on });
  },

  // --- pattern editing --------------------------------------------------

  pushUndo(label) {
    const index = get().currentPatternIndex();
    const pattern = get().currentPattern();
    const stack = get().undoStack.slice(-MAX_UNDO + 1);
    stack.push({ label, patternIndex: index, rows: pattern.rows, data: pattern.data.slice() });
    set({ undoStack: stack, redoStack: [] });
  },

  writeCell(row, channel, field, value, label = 'edit') {
    const { song } = get();
    const index = get().currentPatternIndex();
    const pattern = get().currentPattern();
    if (row < 0 || row >= pattern.rows || channel < 0 || channel >= song.channels) return;

    get().pushUndo(label);
    const off = (row * song.channels + channel) * CELL_STRIDE;
    pattern.data[off + field] = value & 0xff;

    set({ revision: get().revision + 1, dirty: true });
    audioEngine.patchPattern(index, pattern);
  },

  writeNote(row, channel, note, instrument) {
    const { song } = get();
    const index = get().currentPatternIndex();
    const pattern = get().currentPattern();
    if (row < 0 || row >= pattern.rows) return;

    get().pushUndo('note');
    const off = (row * song.channels + channel) * CELL_STRIDE;
    pattern.data[off + CELL_NOTE] = note;
    if (instrument > 0 && note > 0) pattern.data[off + CELL_INSTRUMENT] = instrument;

    set({ revision: get().revision + 1, dirty: true });
    audioEngine.patchPattern(index, pattern);
  },

  clearCell(row, channel, column) {
    const { song } = get();
    const index = get().currentPatternIndex();
    const pattern = get().currentPattern();
    const off = (row * song.channels + channel) * CELL_STRIDE;

    get().pushUndo('clear');
    switch (column) {
      case COL_NOTE:
        pattern.data[off + CELL_NOTE] = 0;
        pattern.data[off + CELL_INSTRUMENT] = 0;
        break;
      case COL_INSTRUMENT_HI:
      case COL_INSTRUMENT_LO:
        pattern.data[off + CELL_INSTRUMENT] = 0;
        break;
      case COL_VOLUME_HI:
      case COL_VOLUME_LO:
        pattern.data[off + CELL_VOLUME] = 0;
        break;
      default:
        pattern.data[off + CELL_EFFECT] = 0;
        pattern.data[off + CELL_PARAM] = 0;
        break;
    }
    set({ revision: get().revision + 1, dirty: true });
    audioEngine.patchPattern(index, pattern);
  },

  insertRow() {
    const { song, cursorRow, cursorChannel } = get();
    const index = get().currentPatternIndex();
    const pattern = get().currentPattern();
    get().pushUndo('insert row');

    const stride = CELL_STRIDE;
    const width = song.channels * stride;
    for (let row = pattern.rows - 1; row > cursorRow; row--) {
      const dst = row * width + cursorChannel * stride;
      const src = (row - 1) * width + cursorChannel * stride;
      pattern.data.copyWithin(dst, src, src + stride);
    }
    const clear = cursorRow * width + cursorChannel * stride;
    pattern.data.fill(0, clear, clear + stride);

    set({ revision: get().revision + 1, dirty: true });
    audioEngine.patchPattern(index, pattern);
  },

  deleteRow() {
    const { song, cursorRow, cursorChannel } = get();
    const index = get().currentPatternIndex();
    const pattern = get().currentPattern();
    get().pushUndo('delete row');

    const stride = CELL_STRIDE;
    const width = song.channels * stride;
    for (let row = cursorRow; row < pattern.rows - 1; row++) {
      const dst = row * width + cursorChannel * stride;
      const src = (row + 1) * width + cursorChannel * stride;
      pattern.data.copyWithin(dst, src, src + stride);
    }
    const clear = (pattern.rows - 1) * width + cursorChannel * stride;
    pattern.data.fill(0, clear, clear + stride);

    set({ revision: get().revision + 1, dirty: true });
    audioEngine.patchPattern(index, pattern);
  },

  transposeSelection(semitones) {
    const { song, selection, cursorRow, cursorChannel } = get();
    const index = get().currentPatternIndex();
    const pattern = get().currentPattern();
    const area = selection ?? {
      startRow: cursorRow,
      endRow: cursorRow,
      startChannel: cursorChannel,
      endChannel: cursorChannel,
    };

    get().pushUndo('transpose');
    for (let row = area.startRow; row <= area.endRow; row++) {
      for (let ch = area.startChannel; ch <= area.endChannel; ch++) {
        const off = (row * song.channels + ch) * CELL_STRIDE;
        const note = pattern.data[off + CELL_NOTE];
        if (note > 0 && note < 97) {
          const next = note + semitones;
          if (next >= 1 && next <= 96) pattern.data[off + CELL_NOTE] = next;
        }
      }
    }
    set({ revision: get().revision + 1, dirty: true });
    audioEngine.patchPattern(index, pattern);
  },

  copySelection() {
    const { song, selection, cursorRow, cursorChannel } = get();
    const pattern = get().currentPattern();
    const area = selection ?? {
      startRow: cursorRow,
      endRow: cursorRow,
      startChannel: cursorChannel,
      endChannel: cursorChannel,
    };
    const rows = area.endRow - area.startRow + 1;
    const channels = area.endChannel - area.startChannel + 1;
    const data = new Uint8Array(rows * channels * CELL_STRIDE);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < channels; c++) {
        const src = ((area.startRow + r) * song.channels + area.startChannel + c) * CELL_STRIDE;
        const dst = (r * channels + c) * CELL_STRIDE;
        data.set(pattern.data.subarray(src, src + CELL_STRIDE), dst);
      }
    }
    set({ clipboard: { rows, channels, data } });
  },

  cutSelection() {
    get().copySelection();
    get().clearSelection();
  },

  clearSelection() {
    const { song, selection, cursorRow, cursorChannel } = get();
    const index = get().currentPatternIndex();
    const pattern = get().currentPattern();
    const area = selection ?? {
      startRow: cursorRow,
      endRow: cursorRow,
      startChannel: cursorChannel,
      endChannel: cursorChannel,
    };

    get().pushUndo('clear block');
    for (let row = area.startRow; row <= area.endRow; row++) {
      for (let ch = area.startChannel; ch <= area.endChannel; ch++) {
        const off = (row * song.channels + ch) * CELL_STRIDE;
        pattern.data.fill(0, off, off + CELL_STRIDE);
      }
    }
    set({ revision: get().revision + 1, dirty: true });
    audioEngine.patchPattern(index, pattern);
  },

  pasteClipboard() {
    const { song, clipboard, cursorRow, cursorChannel } = get();
    if (!clipboard) return;
    const index = get().currentPatternIndex();
    const pattern = get().currentPattern();

    get().pushUndo('paste');
    for (let r = 0; r < clipboard.rows; r++) {
      const row = cursorRow + r;
      if (row >= pattern.rows) break;
      for (let c = 0; c < clipboard.channels; c++) {
        const ch = cursorChannel + c;
        if (ch >= song.channels) break;
        const src = (r * clipboard.channels + c) * CELL_STRIDE;
        const dst = (row * song.channels + ch) * CELL_STRIDE;
        pattern.data.set(clipboard.data.subarray(src, src + CELL_STRIDE), dst);
      }
    }
    set({ revision: get().revision + 1, dirty: true });
    audioEngine.patchPattern(index, pattern);
  },

  undo() {
    const stack = get().undoStack.slice();
    const entry = stack.pop();
    if (!entry) return;
    const { song } = get();
    const pattern = song.patterns[entry.patternIndex];
    if (!pattern) return;

    const redo = get().redoStack.slice();
    redo.push({ label: entry.label, patternIndex: entry.patternIndex, rows: pattern.rows, data: pattern.data.slice() });

    pattern.data.set(entry.data);
    set({ undoStack: stack, redoStack: redo, revision: get().revision + 1, dirty: true });
    audioEngine.patchPattern(entry.patternIndex, pattern);
  },

  redo() {
    const stack = get().redoStack.slice();
    const entry = stack.pop();
    if (!entry) return;
    const { song } = get();
    const pattern = song.patterns[entry.patternIndex];
    if (!pattern) return;

    const undo = get().undoStack.slice();
    undo.push({ label: entry.label, patternIndex: entry.patternIndex, rows: pattern.rows, data: pattern.data.slice() });

    pattern.data.set(entry.data);
    set({ undoStack: undo, redoStack: stack, revision: get().revision + 1, dirty: true });
    audioEngine.patchPattern(entry.patternIndex, pattern);
  },

  // --- settings ---------------------------------------------------------

  setMixerSettings(settings) {
    const next = { ...get().mixerSettings, ...settings };
    set({ mixerSettings: next });
    audioEngine.setMixerSettings(settings);
  },

  setMasterVolume(value) {
    set({ masterVolume: value });
    audioEngine.setMasterVolume(value);
  },

  setLoopSong(on) {
    set({ loopSong: on });
    audioEngine.setPlayerOptions({ loopSong: on });
  },

  setModPanningCommand(on) {
    set({ modPanningCommand: on });
    audioEngine.setPlayerOptions({ modPanningCommand: on });
  },

  toggleMute(channel) {
    const muted = get().mutedChannels.slice();
    muted[channel] = !muted[channel];
    set({ mutedChannels: muted, soloChannel: null });
    audioEngine.setChannelMuted(channel, muted[channel]);
  },

  toggleSolo(channel) {
    const { song, soloChannel } = get();
    if (soloChannel === channel) {
      const muted = new Array(song.channels).fill(false);
      set({ soloChannel: null, mutedChannels: muted });
      audioEngine.clearMutes();
      return;
    }
    const muted = new Array(song.channels).fill(true);
    muted[channel] = false;
    set({ soloChannel: channel, mutedChannels: muted });
    for (let i = 0; i < song.channels; i++) audioEngine.setChannelMuted(i, muted[i]);
  },

  // --- song metadata ----------------------------------------------------

  markDirty() {
    set({ dirty: true, revision: get().revision + 1 });
  },

  setSongName(name) {
    get().song.name = name;
    get().markDirty();
  },

  setInitialSpeed(speed) {
    const value = Math.max(1, Math.min(31, speed));
    get().song.initialSpeed = value;
    get().markDirty();
    audioEngine.patchMeta({ initialSpeed: value });
  },

  setInitialTempo(tempo) {
    const value = Math.max(32, Math.min(255, tempo));
    get().song.initialTempo = value;
    get().markDirty();
    audioEngine.patchMeta({ initialTempo: value });
  },

  setOrderEntry(index, pattern) {
    const { song } = get();
    if (index < 0 || index >= 256) return;
    // Referencing a pattern that does not exist yet creates it, which is how
    // trackers let you build an arrangement before writing the notes.
    while (song.patterns.length <= pattern) {
      song.patterns.push(createPattern(song.patterns[0]?.rows ?? 64, song.channels));
    }
    song.order[index] = pattern;
    get().markDirty();
    audioEngine.patchMeta({ order: song.order });
    audioEngine.loadSong(song);
  },

  setSongLength(length) {
    const { song } = get();
    song.songLength = Math.max(1, Math.min(256, length));
    get().markDirty();
    audioEngine.patchMeta({ songLength: song.songLength });
  },

  setRestartPosition(position) {
    const { song } = get();
    song.restartPosition = Math.max(0, Math.min(song.songLength - 1, position));
    get().markDirty();
    audioEngine.patchMeta({ restartPosition: song.restartPosition });
  },

  addPattern() {
    const { song } = get();
    const rows = song.patterns[0]?.rows ?? 64;
    song.patterns.push(createPattern(rows, song.channels));
    get().markDirty();
    audioEngine.loadSong(song);
    return song.patterns.length - 1;
  },

  addInstrument() {
    const { song } = get();
    song.instruments.push(createInstrument(''));
    get().markDirty();
    audioEngine.loadSong(song);
  },

  replaceInstrument(index, instrument) {
    const { song } = get();
    song.instruments[index] = instrument;
    get().markDirty();
    audioEngine.patchInstrument(index, instrument);
  },
}));

/** Read the five bytes of a cell without pulling the whole pattern into React. */
export function readCell(song: Song, pattern: Pattern, row: number, channel: number) {
  const off = (row * song.channels + channel) * CELL_STRIDE;
  return {
    note: pattern.data[off + CELL_NOTE],
    instrument: pattern.data[off + CELL_INSTRUMENT],
    volume: pattern.data[off + CELL_VOLUME],
    effect: pattern.data[off + CELL_EFFECT],
    param: pattern.data[off + CELL_PARAM],
  };
}
